<template>
  <div class="merchant-login-wrap">
    <div class="login-card">
      <div class="login-header">
        <h2>商家工作台</h2>
        <p>请使用入驻时填写的<strong>联系电话</strong>登录（不是 C 端注册手机号），临时密码见消息中心</p>
      </div>
      <el-form ref="formRef" :model="form" :rules="rules" label-position="top" size="large">
        <el-form-item prop="username">
          <el-input v-model="form.username" placeholder="入驻联系电话" :prefix-icon="User" />
        </el-form-item>
        <el-form-item prop="password">
          <el-input
            v-model="form.password"
            type="password"
            placeholder="请输入登录密码"
            :prefix-icon="Lock"
            show-password
            @keyup.enter="handleLogin"
          />
        </el-form-item>
        <p v-if="loginError" class="login-error">{{ loginError }}</p>
        <el-form-item>
          <el-button type="primary" :loading="loading" class="login-btn" @click="handleLogin">
            登 录
          </el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { User, Lock } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { merchantLogin } from '@/api/merchant/auth'
import { getTenantIdByName } from '@/api/login'
import { setMerchantToken, setTenantId, getTenantId } from '@/utils/auth'
import { afterMerchantLogin } from '@/views/merchant/registry/gateway'
import { useAppStore } from '@/store/modules/app'

defineOptions({ name: 'MerchantLogin' })

const router = useRouter()
const route = useRoute()
const appStore = useAppStore()
let previousDarkMode: boolean | undefined

onMounted(() => {
  previousDarkMode = appStore.getIsDark
  appStore.setIsDark(false, false)
})

onUnmounted(() => {
  if (previousDarkMode !== undefined) {
    appStore.setIsDark(previousDarkMode, false)
  }
})

const formRef = ref()
const loading = ref(false)
const loginError = ref('')
const form = reactive({ username: '', password: '' })
const rules = {
  username: [{ required: true, message: '请输入登录账号', trigger: 'blur' }],
  password: [{ required: true, message: '请输入登录密码', trigger: 'blur' }]
}

watch(
  () => [form.username, form.password],
  () => {
    if (loginError.value) loginError.value = ''
  }
)

async function ensureTenant() {
  if (getTenantId()) return
  const name = import.meta.env.VITE_APP_DEFAULT_LOGIN_TENANT || '紫金山'
  try {
    const id = await getTenantIdByName(name)
    if (id != null) setTenantId(Number(id))
  } catch {
    // 租户接口失败时仍尝试登录；后端可从 token 回填租户
  }
}

function resolveLoginErrorMessage(err: unknown): string {
  const raw =
    (err instanceof Error && err.message) ||
    (typeof err === 'string' ? err : '') ||
    ''
  const msg = raw.trim()
  if (!msg || msg === 'error' || msg === 'Error') return '登录密码错误'
  // 兼容旧后端文案（修改密码错误码曾复用于登录）
  if (msg.includes('原密码不正确')) return '登录密码错误'
  return msg
}

async function handleLogin() {
  const valid = await formRef.value?.validate().catch(() => false)
  if (!valid) return

  loading.value = true
  loginError.value = ''
  try {
    await ensureTenant()
    const data = await merchantLogin(form)
    if (data) {
      setMerchantToken({ accessToken: data.accessToken, refreshToken: data.refreshToken })
      localStorage.setItem('merchantId', String(data.accountId))
      if (data.shopId != null) {
        localStorage.setItem('merchantShopId', String(data.shopId))
      }
      localStorage.setItem('merchantUserType', 'merchant')
      localStorage.removeItem('supplierUserType')
      ElMessage.success('登录成功')
      const redirect = typeof route.query.redirect === 'string' ? route.query.redirect : undefined
      await afterMerchantLogin(router, data.accountId, redirect)
    }
  } catch (err) {
    // 拦截器已弹 toast；此处再落表单内提示，避免登录页无感知
    loginError.value = resolveLoginErrorMessage(err)
  } finally {
    loading.value = false
  }
}
</script>

<style lang="scss" scoped>
/* 登录页底色对齐平台独立窗侧栏深蓝，去掉紫渐变 */
.merchant-login-wrap {
  display: flex;
  min-height: 100vh;
  padding: 20px;
  background:
    radial-gradient(ellipse at 20% 20%, rgb(59 158 255 / 18%), transparent 45%),
    linear-gradient(160deg, #14233a 0%, #1e3a5f 45%, #16304f 100%);
  align-items: center;
  justify-content: center;
}

.login-card {
  width: 420px;
  padding: 48px 40px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 16px 48px rgb(15 23 42 / 28%);
}

.login-header {
  margin-bottom: 36px;
  text-align: center;

  h2 {
    margin: 0 0 8px;
    font-size: 24px;
    font-weight: 700;
    color: #1f2937;
  }

  p {
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: #6b7280;
  }
}

.login-error {
  margin: -8px 0 16px;
  font-size: 13px;
  line-height: 1.4;
  color: #f56c6c;
}

.login-btn {
  width: 100%;
  height: 44px;
  font-size: 15px;
  letter-spacing: 2px;
}

.login-footer {
  margin-top: 24px;
  font-size: 13px;
  line-height: 1.5;
  color: #909399;
  text-align: center;
}
</style>
